/**
 * The two-view stage (CB-5a) — palette.js's list↔detail swap machine, verbatim
 * in behaviour. Both views are PERMANENTLY mounted; the inactive one is
 * `inert` + `visibility:hidden` + `position:absolute` + `pointer-events:none`.
 *
 * Four invariants, each measured on the donor (palette.js:480):
 *  1. Never rebuild — `bodyEl.innerHTML = …` destroyed ~194 <img> elements;
 *     the way back cost ZERO network bytes but 1370ms of re-decode.
 *  2. Never `display:none` (and never `[hidden]`) — that drops the <img>s off
 *     the render tree and forces exactly that re-decode.
 *  3. Geometry is applied INLINE — these elements are created in JS, and a
 *     stale or unloaded stylesheet would otherwise leave both views in flow,
 *     rendering the detail stacked under the whole list.
 *  4. scrollTop is saved and restored EXPLICITLY — relying on the element
 *     keeping its box was measured and does not hold.
 */

/**
 * @param {HTMLElement} container — becomes the stage (position:relative)
 * @returns {{
 *   listView: HTMLElement, detailView: HTMLElement,
 *   showList: (opts?: {focus?: 'origin'}) => void,
 *   showDetail: (opts?: {origin?: HTMLElement|null, resetScroll?: boolean}) => void,
 *   activeView: () => 'list'|'detail'
 * }}
 */
export function createTwoViewStage(container) {
  container.classList.add('pb-coll-stage');
  container.style.position = 'relative';

  const listView = document.createElement('div');
  listView.className = 'pb-coll-view';
  listView.dataset.view = 'list';
  const detailView = document.createElement('div');
  detailView.className = 'pb-coll-view';
  detailView.dataset.view = 'detail';
  container.append(listView, detailView);

  /** The item element the detail was opened from, for Back's focus return. */
  let origin = null;
  const scrollOf = new WeakMap();
  let active = 'list';

  function apply(name) {
    for (const [view, id] of [
      [listView, 'list'],
      [detailView, 'detail']
    ]) {
      const isActive = id === name;
      const wasActive = view.dataset.active === 'true';
      if (wasActive && !isActive) scrollOf.set(view, view.scrollTop);
      view.dataset.active = String(isActive);
      view.toggleAttribute('inert', !isActive);
      view.style.position = isActive ? '' : 'absolute';
      view.style.inset = isActive ? '' : '0';
      view.style.visibility = isActive ? '' : 'hidden';
      view.style.pointerEvents = isActive ? '' : 'none';
      if (isActive && !wasActive) view.scrollTop = scrollOf.get(view) || 0;
    }
    active = name;
  }
  apply('list');

  return {
    listView,
    detailView,
    activeView: () => active,
    showDetail({ origin: from = null, resetScroll = true } = {}) {
      origin = from;
      apply('detail');
      if (resetScroll) detailView.scrollTop = 0;
    },
    showList({ focus } = {}) {
      apply('list');
      if (focus === 'origin' && origin && origin.isConnected) {
        // preventScroll matters twice over: focusing anything inside a scroll
        // container scrolls it into view, and focusing the search field would
        // silently undo the entire scroll restore.
        origin.focus({ preventScroll: true });
      }
      origin = null;
    }
  };
}
